import { Point } from '@angular/cdk/drag-drop';
import { getUUID } from '../id-utils/id.utils';

/**
 * Home for generic helper functions which could be useful anywhere throughout the application
 */

/**
 * @param arr - Array to choose elements from
 * @param weights - Array of weightings to be applied to the elements in arr
 * @returns an (optionally weighted) random element from the provided array
 */
export function getRandomElement(arr: any[], weights?: number[]): any {
	if (!!weights && arr.length !== weights.length) {
		throw new Error('Array and weights should be of equal lengths');
	}

	// If no weights are provided, all elements have the same weight
	if (!weights) {
		return arr[Math.floor(Math.random() * arr.length)];
	}

	const adjustedWights: number[] = [...weights]; // Don't mutate the original weights

	for (let i = 0; i < weights.length; i++) {
		adjustedWights[i] += adjustedWights[i - 1] || 0;
	}

	const random = Math.random() * adjustedWights[weights.length - 1];

	for (let i = 0; i < weights.length; i++) {
		if (adjustedWights[i] > random) {
			return arr[i];
		}
	}
}

/**
 * @param min - Lower bound
 * @param max - Upper bound
 * @returns a random integer in the range [min, max]
 */
export function randIntRange(min: number, max: number) {
	min = Math.ceil(min);
	max = Math.floor(max);
	return Math.floor(min + Math.random() * (max - min + 1));
}

const TITLE_MAX_LENGTH = 64;

/**
 * @param title - String of text to validate
 * @constant TITLE_MAX_LENGTH - Max length of the title
 * @returns an error message, null if valid
 */
export function validateTitleInput(title: string) {
	// Null or empty string check
	if (!title || !title.trim()) {
		return $localize`Title is empty`;
	}
	// Newlines and tabs check
	else if ((title.match(/\r|\n|\t/g) || []).length) {
		return $localize`Title contains newlines or tabs`;
	}
	// Length check
	else if (title.length > TITLE_MAX_LENGTH) {
		return $localize`Title exceeds ${TITLE_MAX_LENGTH} characters`;
	} else {
		return null;
	}
}

/**
 * @param title - String of text to validate
 * @constant TITLE_MAX_LENGTH - Max length of the title
 * @returns an error message, null if valid
 */
export function validateWorkspaceTitleInput(title: string) {
	// "Show all" check
	const showAll = $localize`show all`;
	const failsBaseRequirements = validateTitleInput(title);
	if (failsBaseRequirements) {
		return failsBaseRequirements;
	} else if (title.trim().toLowerCase() === showAll.toLowerCase()) {
		return $localize`Title cannot be "${showAll}"`;
	} else {
		return null;
	}
}

// Generates a deterministic ID for any arbitrary object based on its contents.
export function getObjectHash(object: any): string {
	const flattenedObject = flattenObject(object);
	const orderedObject = Object.keys(flattenedObject)
		.sort()
		.reduce((obj, key) => {
			obj[key] = flattenedObject[key];
			return obj;
		}, {});
	return getUUID(JSON.stringify(orderedObject));
}

function flattenObject(object: any): any {
	const toReturn = {};
	for (const i in object) {
		if (!Object.prototype.hasOwnProperty.call(object, i)) {
			continue;
		}
		if (typeof object[i] == 'object') {
			const flatObject = flattenObject(object[i]);
			for (const x in flatObject) {
				if (!Object.prototype.hasOwnProperty.call(flatObject, x)) {
					continue;
				}
				toReturn[i + '.' + x] = flatObject[x];
			}
		} else {
			toReturn[i] = object[i];
		}
	}
	return toReturn;
}

export const DELIMITERS = /[ \-+*_.,:;|/\\]+/; // Delimiters are [ -+*_.,:;|/\]
export const PUNCTUATION = /[.,/#!$%^&*;:{}=\-_`~()'"]/g;

/**
 * Cleans a string for search purposes by:
 * - Replacing accented characters
 * - Removing punctuation
 * - Removing leading, trailing, and repeated spaces
 * - Lower-casing it
 * @param s - String to be converted
 * @returns - The cleaned string
 */
export function cleanString(s: string) {
	return s
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '') // Replace accented characters with their non-accented versions
		.replace(PUNCTUATION, '') // Remove punctuation
		.replace(/\s{2,}/g, ' ') // Replace concurrent spaces with with single spaces
		.trim() // Remove leading/trailing spaces
		.toLowerCase(); // Lower-case
}

/**
 * Tokenizes a string into an array of terms
 * @param s - String to be converted
 * @returns - The tokenization of the string
 */
export function tokenizeString(s: string) {
	return s
		.split(DELIMITERS) // Split phrase into array of words
		.map(t => cleanString(t)) // Clean each word
		.filter(t => t.length > 0); // Remove empty strings
}

/**
 * Checks whether the argument is an array or not
 * @param x - Argument to be checked
 * @returns - true if the argument is an array, false otherwise
 */
export function isArray(x: any) {
	return x?.constructor.name === 'Array';
}

/**
 * Checks whether the argument is an object or not
 * @param x - Argument to be checked
 * @returns - true if the argument is an object, false otherwise
 */
export function isObject(x: any) {
	return x?.constructor.name === 'Object';
}

/**
 * Checks whether the argument is a number or not
 * @param x - Object to be checked
 * @returns - true if the argument is a number, false otherwise
 */
export function isNumber(x: any) {
	return x?.constructor.name === 'Number';
}

/**
 * Checks whether the argument is a string or not
 * @param x - Argument to be checked
 * @returns - true if the argument is an string, false otherwise
 */
export function isString(x: any) {
	return x?.constructor.name === 'String';
}

/**
 * Checks whether the argument is a boolean or not
 * @param x - Argument to be checked
 * @returns - true if the argument is a boolean, false otherwise
 */
export function isBoolean(x: any) {
	return x?.constructor.name === 'Boolean';
}

/**
 * Checks whether the argument is a function or not
 * @param x - Argument to be checked
 * @returns - true if the argument is a function, false otherwise
 */
export function isFunction(x: any) {
	return x?.constructor.name === 'Function';
}

/**
 * Checks whether the argument is a primitive or not
 * @param x - Argument to be checked
 * @returns - true if the argument is a primitive, false otherwise
 */
export function isPrimitive(x: any) {
	return x !== Object(x);
}

/**
 * Checks whether one point is within the specified radius of another point
 * @param p1 - One point
 * @param p2 - Another point
 * @param radius - Allowable radius
 * @returns - true if the point is within the radius, false otherwise
 */
export function withinRadius(p1: Point, p2: Point, radius: number) {
	return Math.hypot(p1.x - p2.x, p1.y - p2.y) < radius;
}

/**
 * Checks if the current device is mobile
 * @returns - true if the current device is mobile, false otherwise
 */
export function isMobile() {
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
